import React, { useEffect } from "react"
import ArbeidMedVeglisterForm from "./Arbeid/Form"

import { HashRouter as Router, Route, Switch } from "react-router-dom"

import PubliserVeglister from "./Publiser/Form"
import { apiPath, apiURLWithParams, fetchFylkerOgKommuner } from "../api"
import { getOmraader } from "@nvdb-rapport/shared/src/api"
import {
  parseAndSetFylkerOgKommuner,
  FylkerOgKommuner,
  AreaMappings,
} from "../utils/setFylkerOgKommuner"
import { SearchResult } from "./Result/SearchResult"
import Nav from "./Layout/Nav"
import Alert from "@material-ui/lab/Alert"
import AlertTitle from "@material-ui/lab/AlertTitle"
import Head from "@nvdb-rapport/shared/src/components/Head"
import { alertBeforeUnload } from "../utils/alertBeforeUnload"
import { Main } from "../styles/Container"
import BrowserNotSupported from "./BrowserNotSupported"
import AppLoading from "./AppLoading"
import NotFound from "./404"
import { useInterval } from "@nvdb-rapport/shared/src/hooks/useInterval"

import { AppState, UIModel, UIAction } from "../store/ui"

import { SearchParameters } from "../interfaces/SearchParameters.interface"
import { PubliseringModel, PubliseringAction } from "../store/publisering"
import { ArbeidAction, ArbeidModel } from "../store/arbeid"

const rapportHeaders = {
  "X-client": "NVDB-Rapporter-Frontend-Gammel",
}

const POLL_INTERVAL = 1000

interface ArbeidMedVeglisterFormProps {
  arbeidState: ArbeidModel
  arbeidDispatch: React.Dispatch<ArbeidAction>
  uiState: UIModel
  uiDispatch: React.Dispatch<UIAction>
  publiseringState: PubliseringModel
  publiseringDispatch: React.Dispatch<PubliseringAction>
}

function BruksklasseSearch({
  uiState,
  uiDispatch,
  arbeidState,
  arbeidDispatch,
  publiseringState,
  publiseringDispatch,
}: ArbeidMedVeglisterFormProps) {
  useEffect(() => {
    if (uiState.appState === AppState.DATA_PENDING) {
      window.addEventListener("beforeunload", alertBeforeUnload)
    } else {
      window.removeEventListener("beforeunload", alertBeforeUnload)
    }
    return () => {
      window.removeEventListener("beforeunload", alertBeforeUnload)
    }
  }, [uiState.appState])

  useEffect(() => {
    if (uiState.appState === AppState.LOADING) {
      ;(async () => {
        const decodedOmraader = await getOmraader(apiPath)
        uiDispatch({ type: "SetOmraader", payload: decodedOmraader })
      })()

      fetchFylkerOgKommuner()
        .then(res => {
          if (res.status !== 200) {
            uiDispatch({
              type: "AddError",
              payload: {
                title: `Status: ${res.status}`,
                message: `Kunne ikke hente fylker og kommuner (${res.statusText})`,
              },
            })

            return
          }
          res.json().then((data: FylkerOgKommuner) => {
            const areaMappings: AreaMappings = parseAndSetFylkerOgKommuner(data)
            uiDispatch({ type: "SetAreaMappings", payload: areaMappings })
            uiDispatch({ type: "UpdateAppState", payload: AppState.APP_READY })
          })
        })
        .catch(reason => {
          console.error(reason)
          uiDispatch({
            type: "AddError",
            payload: {
              title: `Kunne ikke hente fylker og kommuner`,
              message: `NVDB Rapport-APIet svarer ikke (${reason})`,
            },
          })

          return
        })
    }
  }, [uiDispatch, uiState.appState])

  const onSearchClick = (params: SearchParameters): void => {
    const apiPath = apiURLWithParams(params)
    uiDispatch({
      type: "SetSearchResult",
      payload: {
        tableData: null,
        downloadLinkXLSX: "",
        downloadLinkDOCX: "",
        downloadLinkCSV: "",
        durationMs: 0,
        durationMsString: "",
      },
    })

    fetch(apiPath, {
      headers: rapportHeaders,
    })
      .then(res => {
        if (res.status !== 202) {
          uiDispatch({
            type: "AddError",
            payload: {
              title: `Status: ${res.status}`,
              message: `Noe gikk galt med med innhenting av data (${res.statusText})`,
            },
          })

          return
        }
        res.json().then(data => {
          uiDispatch({ type: "JobPending", payload: data.id })
          uiDispatch({ type: "RequestData" })
        })
      })
      .catch(reason => {
        uiDispatch({
          type: "AddError",
          payload: {
            title: `Feil ved generering av veglister`,
            message: `NVDB Rapport API svarer ikke (${reason})`,
          },
        })
      })
  }

  useInterval(
    () => {
      const url = `${apiPath}/poll/${uiState.jobId}`
      fetch(url, {
        headers: rapportHeaders,
      })
        .then(response => {
          return response.json()
        })
        .then(result => {
          if (result["status"] === "SUCCESS") {
            uiDispatch({ type: "UpdateAppState", payload: AppState.DATA_READY })
            uiDispatch({
              type: "SetSearchResult",
              payload: {
                downloadLinkXLSX:
                  apiPath +
                  "/nedlasting/" +
                  result.rapport.nedlastLinkByFiltype.XLSX +
                  "?filtype=XLSX",
                downloadLinkDOCX:
                  apiPath +
                  "/nedlasting/" +
                  result.rapport.nedlastLinkByFiltype.DOCX +
                  "?filtype=DOCX",
                downloadLinkCSV:
                  apiPath +
                  "/nedlasting/" +
                  result.rapport.nedlastLinkByFiltype.CSV +
                  "?filtype=CSV",
                tableData: result.rapport,
                durationMs: result.durationMs,
                durationMsString: result.durationMsString,
              },
            })

            return true
          } else if (result["status"] === "ERROR") {
            uiDispatch({
              type: "AddError",
              payload: {
                title: `FEIL (${result.durationMsString})`,
                message: ` Vegliste feilet! (${result.error})`,
              },
            })
            uiDispatch({
              type: "SetSearchResult",
              payload: {
                downloadLinkXLSX: "",
                downloadLinkDOCX: "",
                downloadLinkCSV: "",
                tableData: null,
                durationMs: result.durationMs,
                durationMsString: result.durationMsString,
              },
            })

            return true
          } else if (result["status"] === "PENDING") {
            uiDispatch({ type: "Poll", payload: result })

            return false
          }
        })
        .catch(error => {
          uiDispatch({
            type: "AddError",
            payload: {
              title: `FEIL (${uiState.searchResult.durationMsString})`,
              message: `NVDB Rapport API svarer ikke (${error})`,
            },
          })

          console.error("ERROR", error)
        })
    },
    uiState.appState === AppState.DATA_PENDING ? POLL_INTERVAL : null
  )

  if (uiState.appState === AppState.UNSUPPORTED_BROWSER) {
    return <BrowserNotSupported />
  }

  // Waiting for fylker and kommuner to load
  if (uiState.appState === AppState.LOADING) {
    return <AppLoading />
  }

  return (
    <>
      <Head siteTitle="Veglister" />

      <Router>
        <Nav />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            marginBottom: "3rem",
          }}
        >
          <Main>
            <Switch>
              <Route path="/arbeid">
                <ArbeidMedVeglisterForm
                  onSearchClick={onSearchClick}
                  uiDispatch={uiDispatch}
                  uiState={uiState}
                  arbeidDispatch={arbeidDispatch}
                  arbeidState={arbeidState}
                />
              </Route>
              <Route path="/generer">
                <PubliserVeglister
                  onSearchClick={onSearchClick}
                  uiState={uiState}
                  uiDispatch={uiDispatch}
                  publiseringState={publiseringState}
                  publiseringDispatch={publiseringDispatch}
                  arbeidState={arbeidState}
                  arbeidDispatch={arbeidDispatch}
                />
              </Route>
              <Route exact path="/">
                <p>
                  Velg 'Arbeid med veglister' eller 'Generer veglister for
                  publisering'
                </p>
              </Route>
              <Route component={NotFound}></Route>
            </Switch>
            {uiState.appState === AppState.ERROR && uiState.errors.length > 0 && (
              <div style={{ margin: "5rem 0" }}>
                {uiState.errors.map((errorData, index) => (
                  <Alert severity="error" key={index}>
                    <AlertTitle style={{ fontWeight: "bold" }}>
                      {errorData.title}
                    </AlertTitle>
                    {errorData.message}
                  </Alert>
                ))}
              </div>
            )}
          </Main>

          <SearchResult uiState={uiState} />
        </div>
      </Router>
    </>
  )
}

export default BruksklasseSearch
